import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useClientAccess } from '../../hooks/useClientAccess';
import { useI18n } from '../../contexts/I18nContext';
import config from '../../config/env';
import { environmentFromOAuthClientId } from '../../utils/clientGroups';

/**
 * Explains the one failure state that otherwise looks like a broken app.
 *
 * A user with a valid role but no client-scope membership passes every
 * permission check and then sees empty tables everywhere: no devices, zero
 * statistics, no clients, no way to add a user. Nothing distinguishes it
 * from "your organisation has no data yet", so it reads as a bug.
 *
 * It happens whenever the account sits in /Clients/{uuid}/members without
 * the matching /Clients/{uuid}/{env} — the state a revoked access leaves
 * behind, and the state a manually created account starts in.
 */
export const NoClientAccessBanner: React.FC = () => {
  const { isAuthenticated, loading, user } = useAuth();
  const { hasAnyClientAccess, isSuperAdmin, hasApplicationRole } = useClientAccess();
  const { t } = useI18n();

  // SuperAdmin is unrestricted by design and legitimately has no groups.
  if (loading || !isAuthenticated || isSuperAdmin || hasAnyClientAccess) {
    return null;
  }

  // Someone with no application role on this environment has a different
  // problem — the API answers 403, not empty — and is shut out before
  // reaching any screen. Do not offer them this explanation.
  //
  // This cannot test `user.roles.length`: the realm always adds
  // `default-roles-…`, `offline_access` and `uma_authorization`, so that
  // list is never empty and the old check never fired.
  if (!hasApplicationRole) {
    return null;
  }

  const env = environmentFromOAuthClientId(config.oauth.clientId);

  return (
    <div className="no-client-access-banner" role="status" data-testid="no-client-access-banner">
      <span className="no-client-access-icon" aria-hidden="true">⚠</span>
      <div className="no-client-access-body">
        <strong>{t('errors.noClientAccessTitle')}</strong>
        <p>{t('errors.noClientAccessBody', { env })}</p>
      </div>
    </div>
  );
};
